import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Mail, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { glassCardStatic } from '@/utils/admin-glass-styles';

interface EmailTestResult {
  type: string;
  status: 'success' | 'error' | 'pending';
  message: string;
  timestamp: string;
}

export default function EmailNotificationTest() {
  const [testBookingId, setTestBookingId] = useState('');
  const [testType, setTestType] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [testResults, setTestResults] = useState<EmailTestResult[]>([]);
  const { toast } = useToast();

  const emailTestTypes = [
    { value: 'bookingStatusChange', label: '预约状态变更通知', description: '测试取消预约邮件通知' },
    { value: 'refundNotification', label: '退款通知', description: '测试退款处理邮件通知' },
    { value: 'bookingReminder', label: '预约提醒', description: '测试预约提醒邮件' },
    { value: 'bookingConfirmation', label: '预约确认', description: '测试预约确认邮件' }
  ];

  const addTestResult = (type: string, status: 'success' | 'error' | 'pending', message: string) => {
    const result: EmailTestResult = {
      type,
      status,
      message,
      timestamp: new Date().toLocaleString()
    };
    setTestResults(prev => [result, ...prev]);
  };

  const testEmailNotification = async () => {
    if (!testType || !testBookingId) {
      toast({
        title: "参数缺失",
        description: "请选择测试类型并输入预约ID",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    addTestResult(testType, 'pending', '开始发送测试邮件...');

    try {
      // First get booking details to verify it exists
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .select(`
          *,
          services(service_name),
          clients(first_name, last_name, timezone),
          practitioners(first_name, last_name, timezone)
        `)
        .eq('id', testBookingId)
        .single();

      if (bookingError || !booking) {
        throw new Error(`预约不存在或无权访问: ${bookingError?.message}`);
      }

      addTestResult(testType, 'success', `找到预约: ${booking.services.service_name}`);

      // Test email sending based on type
      let emailPayload: any = {
        template_type: testType,
        booking_id: testBookingId
      };

      switch (testType) {
        case 'bookingStatusChange':
          emailPayload = {
            ...emailPayload,
            client_id: booking.client_id,
            practitioner_id: booking.practitioner_id,
            old_status: booking.status,
            new_status: 'Cancelled by Client'
          };
          break;
        case 'refundNotification':
          emailPayload = {
            ...emailPayload,
            client_id: booking.client_id,
            practitioner_id: booking.practitioner_id,
            refund_status: 'processed'
          };
          break;
        default:
          emailPayload = {
            ...emailPayload,
            client_id: booking.client_id,
            practitioner_id: booking.practitioner_id
          };
      }

      console.log('Sending email with payload:', emailPayload);

      const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-email', {
        body: emailPayload
      });

      if (emailError) {
        throw new Error(`邮件发送失败: ${emailError.message}`);
      }

      addTestResult(testType, 'success', `邮件发送成功! 结果: ${JSON.stringify(emailResult)}`);
      
      toast({
        title: "测试成功",
        description: "邮件通知已发送，请检查收件箱",
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      console.error('Email test error:', error);
      addTestResult(testType, 'error', errorMessage);
      
      toast({
        title: "测试失败",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const getStatusIcon = (status: 'success' | 'error' | 'pending') => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getStatusBadge = (status: 'success' | 'error' | 'pending') => {
    const variants = {
      success: 'default',
      error: 'destructive',
      pending: 'secondary'
    } as const;
    
    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  return (
    <Card className={`w-full max-w-4xl mx-auto ${glassCardStatic}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          邮件通知功能测试
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Test Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="test-type">邮件类型</Label>
            <Select value={testType} onValueChange={setTestType}>
              <SelectTrigger>
                <SelectValue placeholder="选择邮件测试类型" />
              </SelectTrigger>
              <SelectContent>
                {emailTestTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex flex-col">
                      <span>{type.label}</span>
                      <span className="text-xs text-muted-foreground">{type.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="booking-id">预约ID</Label>
            <Input
              id="booking-id"
              placeholder="输入测试用的预约ID"
              value={testBookingId}
              onChange={(e) => setTestBookingId(e.target.value)}
            />
          </div>
        </div>

        {/* Test Actions */}
        <div className="flex gap-2">
          <Button 
            onClick={testEmailNotification} 
            disabled={isLoading || !testType || !testBookingId}
            className="flex items-center gap-2"
          >
            <Mail className="h-4 w-4" />
            {isLoading ? '发送中...' : '发送测试邮件'}
          </Button>
          
          {testResults.length > 0 && (
            <Button 
              variant="outline" 
              onClick={clearResults}
            >
              清除结果
            </Button>
          )}
        </div>

        {/* Test Results */}
        {testResults.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">测试结果</h3>
              <Badge variant="outline">{testResults.length} 条记录</Badge>
            </div>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {testResults.map((result, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-3 border rounded-lg bg-muted/30"
                >
                  {getStatusIcon(result.status)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{result.type}</span>
                      {getStatusBadge(result.status)}
                      <span className="text-xs text-muted-foreground">{result.timestamp}</span>
                    </div>
                    <p className="text-sm text-muted-foreground break-words">{result.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">使用说明：</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• 选择要测试的邮件类型</li>
            <li>• 输入有效的预约ID（可以从数据库中查找）</li>
            <li>• 点击"发送测试邮件"按钮</li>
            <li>• 检查相关用户的邮箱收到测试邮件</li>
            <li>• 查看测试结果确认邮件发送状态</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}